import Vue from 'vue';

import router from '@core/router';
import { Auth } from '@core/auth';

import { isLive } from '@utils/env';

import i18n from '@plugins/i18n';

import Adviser from '@/models/Adviser';
import Investor from '@/models/Investor';

const mockApi = isLive() ? false : localStorage.getItem('mock_api') === 'true';

export default {
    app: {
        namespaced: true,
        state: {
            routing: false,
            //
            mock_api: mockApi,
            query_selector: {},
            query_selector_data: {},
            //
            modal: {
                active: false,
                key: null,
                props: {},
                callback: () => {}
            },
            //
            show_media_library: false,
            media_library_heading: null,
            media_library_collection: null,
            media_library_model: null,
            media_library_selected_callback: () => {},
            media_library_multiple: false,
            media_library_preselected: null,
            //
            table_display_format: null,
            //
            request_user_feedback_modal: false,
            request_user_feedback_modal_options: {},
            //
            loading: {
                active: false,
                title: null,
                text: null,
                progress: null,
                success: false
            },
            progress_queue: {
                active: false,
                title: null,
                position: 'right',
                items: []
            },
            breadcrumbs: [],
            aborted: [],
            unread_notifications_count: 0,
            current_route: null
        },
        getters: {
            routing: state => state.routing,
            mock_api: state => state.mock_api,
            query_selector: state => key => {
                return state.query_selector?.[`${key}`];
            },
            query_selector_data: state => key => {
                return state.query_selector_data?.[`${key}`];
            },
            query_selector_keys: state => Object.keys(state.query_selector),
            //
            modal: state => state.modal,
            //
            show_media_library: state => state.show_media_library,
            media_library_heading: state => state.media_library_heading,
            media_library_collection: state => state.media_library_collection,
            media_library_model: state => state.media_library_model,
            media_library_selected_callback: state => state.media_library_selected_callback,
            media_library_multiple: state => state.media_library_multiple,
            media_library_preselected: state => state.media_library_preselected,
            //
            table_display_format: state => state.table_display_format,
            //
            request_user_feedback_modal: state => state.request_user_feedback_modal,
            request_user_feedback_modal_options: state => state.request_user_feedback_modal_options,
            //
            loading: state => state.loading,
            progress_queue: state => state.progress_queue,
            has_progress_queue: state => state.progress_queue.active,
            breadcrumbs: state => state.breadcrumbs,
            aborted: state => state.aborted,
            has_selected_investor: state => !!state.query_selector_data.investor_id,
            investor_has_additional_needs: state => {
                let investor = null;

                if (Auth().is_investor) {
                    investor = Auth().investor;
                } else {
                    investor = state.query_selector_data.investor_id;
                }

                if (!investor) {
                    return false;
                }

                investor = investor instanceof Investor ? investor : new Investor(investor);
                return investor.has_additional_needs;
            },
            investor_has_portfolio_restricted: state => {
                let investor = null;

                if (Auth().is_investor) {
                    investor = Auth().investor;
                } else {
                    investor = state.query_selector_data.investor_id;
                }

                if (!investor) {
                    return false;
                }

                investor = investor instanceof Investor ? investor : new Investor(investor);
                return investor.is_portfolio_restricted;
            },
            investor_has_portfolio_warning: state => {
                let investor = null;

                if (Auth().is_investor) {
                    investor = Auth().investor;
                } else {
                    investor = state.query_selector_data.investor_id;
                }

                if (!investor) {
                    return false;
                }

                investor = investor instanceof Investor ? investor : new Investor(investor);
                return investor.is_portfolio_warning;
            },
            some_investors_have_portfolio_restrictions() {
                if (!Auth().is_adviser) {
                    return false;
                }

                let adviser = Auth().adviser;

                if (!adviser) {
                    return false;
                }

                adviser = adviser instanceof Adviser ? adviser : new Adviser(adviser);
                return adviser.some_investors_have_portfolio_restrictions;
            },
            export_banners: (state, getters) => {
                const banners = [];

                if (getters.investor_has_portfolio_restricted || getters.investor_has_portfolio_warning) {
                    if (Auth().is_investor) {
                        banners.push({
                            text: i18n.t('_page.investor_portfolio_restricted.investor_notice'),
                            type: 'warning'
                        });
                    } else {
                        banners.push({
                            text: i18n.t('_page.investor_portfolio_restricted.adviser_notice'),
                            type: 'warning'
                        });
                    }
                } else if (!getters.has_selected_investor && getters.some_investors_have_portfolio_restrictions) {
                    banners.push({
                        text: i18n.t('_page.investor_portfolio_restricted.adviser_notice'),
                        type: 'warning'
                    });
                }

                return banners;
            },
            unread_notifications_count: state => state.unread_notifications_count,
            current_route: state => state.current_route
        },
        actions: {
            startRouting({ commit }) {
                commit('SET_ROUTING', true);
            },
            endRouting({ commit }) {
                commit('SET_ROUTING', false);
            },
            setCurrentRoute({ commit }, route) {
                commit('SET_CURRENT_ROUTE', route);
            },
            //
            toggleMockApi({ commit }) {
                commit('TOGGLE_MOCK_API');
            },
            clearMockData({ commit }) {
                commit('CLEAR_MOCK_DATA');
            },
            //
            setModal({ commit }, { key, props = {}, callback = () => {} }) {
                commit('SET_MODAL', { key, props, callback });
            },
            setModalVisibility({ commit }, active) {
                commit('SET_MODAL_VISIBILITY', active);
            },
            resetModal({ commit }) {
                commit('RESET_MODAL');
            },
            //
            setMediaLibraryVisibility({ commit }, active) {
                commit('SET_MEDIA_LIBRARY_VISIBILITY', active);
            },
            setMediaLibrary(
                { commit },
                { collection, onSelected, multiple = false, preselected = null, heading = null }
            ) {
                commit('SET_MEDIA_LIBRARY_VISIBILITY', false);
                commit('SET_MEDIA_LIBRARY_HEADING', heading);
                commit('SET_MEDIA_LIBRARY_COLLECTION', collection);
                commit('SET_MEDIA_LIBRARY_SELECTED_CALLBACK', onSelected);
                commit('SET_MEDIA_LIBRARY_MULTIPLE', multiple);
                commit('SET_MEDIA_LIBRARY_PRESELECTED', preselected);
            },
            resetMediaLibrary({ commit }) {
                commit('SET_MEDIA_LIBRARY_VISIBILITY', false);
                commit('SET_MEDIA_LIBRARY_HEADING', null);
                commit('SET_MEDIA_LIBRARY_COLLECTION', null);
                commit('SET_MEDIA_LIBRARY_SELECTED_CALLBACK', () => {});
                commit('SET_MEDIA_LIBRARY_MULTIPLE', false);
                commit('SET_MEDIA_LIBRARY_PRESELECTED', null);
            },
            showUserFeedbackModal({ commit }, options = {}) {
                commit('SHOW_USER_FEEDBACK_MODAL', options);
            },
            hideUserFeedbackModal({ commit }) {
                commit('HIDE_USER_FEEDBACK_MODAL');
            },
            addQuerySelector({ commit }, querySelector = { key: null, value: null, data: null }) {
                if (querySelector.key) {
                    commit('SET_QUERY_SELECTOR', querySelector);
                }

                // https://growthinvest.atlassian.net/browse/CP-4460
                const addingInvestorId = querySelector.key === 'investor_id' && querySelector.value;
                const investorRestricted = querySelector.data && querySelector.data.is_portfolio_restricted;
                const isNonGiAdviser = Auth().is_adviser && !Auth().is_gi;

                if (addingInvestorId && investorRestricted && isNonGiAdviser) {
                    const $route = router.currentRoute;

                    if (
                        ($route.path.startsWith('/portfolio') && !$route.path.startsWith('/portfolio/clients')) ||
                        $route.path.startsWith('/accounts') ||
                        $route.path.startsWith('/transactions') ||
                        $route.path.startsWith('/reports/client-portfolio')
                    ) {
                        router.push({
                            name: 'AdviserInvestorPortfolioRestrictedNotice',
                            query: {
                                redirect: encodeURIComponent(
                                    JSON.stringify({
                                        page: $route.meta.heading || null,
                                        name: $route.name,
                                        query: $route.query
                                    })
                                )
                            }
                        });
                    }
                }
            },
            removeQuerySelectors({ commit, state }) {
                Object.keys(state.query_selector).forEach(key => {
                    commit('SET_QUERY_SELECTOR', { key, value: null, data: null });
                });
            },
            removeQuerySelector({ commit }, key) {
                commit('SET_QUERY_SELECTOR', { key, value: null, data: null });
            },
            setLoading({ commit }, loading = {}) {
                loading = {
                    active: false,
                    title: null,
                    text: null,
                    progress: null,
                    buttons: [],
                    success: false,
                    ...loading
                };

                if (loading === false) {
                    loading = { active: false, title: null, text: null, progress: null, buttons: [] };
                }

                if (loading === true) {
                    loading = { active: true, title: null, text: null, progress: null, buttons: [] };
                }

                commit('SET_LOADING', loading);
            },
            setLoadingTitle({ commit }, title = null) {
                commit('SET_LOADING_TITLE', title);
            },
            setLoadingText({ commit }, text = null) {
                commit('SET_LOADING_TEXT', text);
            },
            setLoadingProgress({ commit }, progress = null) {
                commit('SET_LOADING_PROGRESS', progress);
            },
            setLoadingButtons({ commit }, buttons = []) {
                commit('SET_LOADING_BUTTONS', buttons);
            },
            setLoadingSuccess({ commit }, success = true) {
                commit('SET_LOADING_SUCCESS', success);
            },
            //
            setProgressQueue({ commit }, progressQueue = {}) {
                progressQueue = {
                    active: false,
                    title: null,
                    position: 'right',
                    items: [],
                    ...progressQueue
                };

                if (progressQueue === false) {
                    progressQueue = { active: false, title: null, position: 'right', items: [] };
                }

                if (progressQueue === true) {
                    progressQueue = { active: true, title: null, position: 'right', items: [] };
                }

                commit('SET_PROGRESS_QUEUE', progressQueue);
            },
            setProgressQueueTitle({ commit }, title = null) {
                commit('SET_PROGRESS_QUEUE_TITLE', title);
            },
            setProgressQueuePosition({ commit }, position = null) {
                commit('SET_PROGRESS_QUEUE_POSITION', position);
            },
            setProgressQueueItems({ commit }, items = []) {
                const ids = items.map(item => item.id);
                items = items.filter((item, index) => ids.indexOf(item.id) === index);
                commit('SET_PROGRESS_QUEUE_ITEMS', items);
            },
            prependProgressQueueItems({ commit, state }, items = []) {
                const oldItems = state.progress_queue.items.filter(item => !items.map(i => i.id).includes(item.id));
                items = items.concat(oldItems);

                const ids = items.map(item => item.id);
                items = items.filter((item, index) => ids.indexOf(item.id) === index);
                commit('SET_PROGRESS_QUEUE_ITEMS', items);
            },
            updateProgressQueueItem({ commit }, { id, data }) {
                commit('UPDATE_PROGRESS_QUEUE_ITEM', { id, data });
            },
            //
            setBreadcrumbs({ commit }, breadcrumbs = []) {
                commit('SET_BREADCRUMBS', breadcrumbs);
            },
            abort({ commit }, id) {
                commit('ABORT', id);
            },
            abortClean({ commit }, id) {
                commit('ABORT_CLEAN', id);
            },
            setUnreadNotificationsCount({ commit }, count = 0) {
                commit('SET_UNREAD_NOTIFICATIONS_COUNT', count);

                // Update the auth store with the new notifications count
                const auth = Auth();

                if (auth.has_account && auth._account.notifications) {
                    // Update the account
                    auth._account.notifications.unread = count;
                    auth._organisation.accounts.forEach(account => {
                        if (account.adviser_id === auth._account.adviser_id && account.notifications) {
                            account.notifications.unread = count;
                        }
                    });
                    if (auth.has_user && Array.isArray(auth._user.organisations)) {
                        auth._user.organisations.forEach(organisation => {
                            if (organisation.id === auth._organisation.id) {
                                organisation.accounts.forEach(account => {
                                    if (account.adviser_id === auth._account.adviser_id && account.notifications) {
                                        account.notifications.unread = count;
                                    }
                                });
                            }
                        });
                    }
                    auth.$save();

                    let totalUnread = auth.accounts.reduce((total, account) => {
                        return total + (account.notifications.unread || 0);
                    }, 0);

                    // Update the organisation
                    if (auth.has_organisation && auth._organisation.notifications) {
                        auth._organisation.notifications.unread = totalUnread;
                        if (auth.has_user && Array.isArray(auth._user.organisations)) {
                            auth._user.organisations.forEach(organisation => {
                                if (organisation.id === auth._organisation.id) {
                                    organisation.notifications.unread = totalUnread;
                                }
                            });
                        }
                    }
                    auth.$save();
                } else if (auth.has_organisation && auth._organisation.notifications) {
                    // Update the organisation
                    auth._organisation.notifications.unread = count;
                    if (auth.has_user && Array.isArray(auth._user.organisations)) {
                        auth._user.organisations.forEach(organisation => {
                            if (organisation.id === auth._organisation.id) {
                                organisation.notifications.unread = count;
                            }
                        });
                    }
                    auth.$save();
                }

                return count;
            }
        },
        mutations: {
            SET_ROUTING(state, routing) {
                state.routing = routing;
            },
            SET_CURRENT_ROUTE(state, route) {
                state.current_route = route;
            },
            //
            TOGGLE_MOCK_API(state) {
                if (isLive()) {
                    state.mock_api = false;
                    return;
                }

                state.mock_api = !state.mock_api;
                localStorage.setItem('mock_api', state.mock_api);
                window.location.reload();
            },
            CLEAR_MOCK_DATA() {
                Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('mock_') && key !== 'mock_api') {
                        localStorage.removeItem(key);
                    }
                });

                window.location.reload();
            },
            //
            SET_MODAL_VISIBILITY(state, active) {
                state.modal.active = active;
            },
            SET_MODAL(state, { key, props = {}, callback = () => {} }) {
                state.modal = { active: true, key, props, callback };
            },
            RESET_MODAL(state) {
                state.modal = { active: false, key: null, props: {}, callback: () => {} };
            },
            //
            SET_MEDIA_LIBRARY_VISIBILITY(state, active) {
                state.show_media_library = active;
            },
            SET_MEDIA_LIBRARY_HEADING(state, heading) {
                state.media_library_heading = heading;
            },
            SET_MEDIA_LIBRARY_COLLECTION(state, collection) {
                state.media_library_collection = collection;
                state.media_library_model = collection ? collection.constructor.model : null;
            },
            SET_MEDIA_LIBRARY_MODEL(state, model) {
                state.media_library_model = model;
            },
            SET_MEDIA_LIBRARY_SELECTED_CALLBACK(state, callback) {
                state.media_library_selected_callback = callback;
            },
            SET_MEDIA_LIBRARY_MULTIPLE(state, multiple) {
                state.media_library_multiple = multiple;
            },
            SET_MEDIA_LIBRARY_PRESELECTED(state, preselected) {
                state.media_library_preselected = preselected;
            },
            //
            SHOW_USER_FEEDBACK_MODAL(state, options = {}) {
                state.request_user_feedback_modal = true;
                state.request_user_feedback_modal_options = options;
            },
            HIDE_USER_FEEDBACK_MODAL(state) {
                state.request_user_feedback_modal = false;
                state.request_user_feedback_modal_options = {};
            },
            //
            SET_LOADING(state, { active, title, text, progress, buttons }) {
                state.loading = { active, title, text, progress, buttons };
            },
            SET_LOADING_TITLE(state, title) {
                state.loading.title = title;
            },
            SET_LOADING_TEXT(state, text) {
                state.loading.text = text;
            },
            SET_LOADING_PROGRESS(state, progress) {
                state.loading.progress = progress;
            },
            SET_LOADING_BUTTONS(state, buttons) {
                state.loading.buttons = buttons;
            },
            SET_LOADING_SUCCESS(state, success) {
                state.loading.success = success;
            },
            //
            SET_PROGRESS_QUEUE(state, { active, title, position, items }) {
                state.progress_queue = { active, title, position, items };
            },
            SET_PROGRESS_QUEUE_TITLE(state, title) {
                state.progress_queue.title = title;
            },
            SET_PROGRESS_QUEUE_POSITION(state, position) {
                state.progress_queue.position = position;
            },
            SET_PROGRESS_QUEUE_ITEMS(state, items) {
                state.progress_queue.items = items;
            },
            UPDATE_PROGRESS_QUEUE_ITEM(state, { id, data }) {
                const index = state.progress_queue.items.findIndex(item => item.id === id);
                if (index !== -1) {
                    const item = state.progress_queue.items[`${index}`];

                    for (const key in data) {
                        item[`${key}`] = data[`${key}`];
                    }

                    Vue.set(state.progress_queue.items, index, item);
                }
            },
            //
            SET_QUERY_SELECTOR(state, querySelector = {}) {
                if (querySelector.key === undefined) {
                    console.warn('SET_QUERY_SELECTOR mutation called without a "key" in the payload.');
                    return;
                }

                Vue.set(state.query_selector, querySelector.key, querySelector.value);
                Vue.set(state.query_selector_data, querySelector.key, querySelector.data);
            },
            SET_BREADCRUMBS(state, breadcrumbs = []) {
                state.breadcrumbs = breadcrumbs;
            },
            //
            ABORT(state, id) {
                if (!state.aborted.includes(id)) {
                    state.aborted.push(id);
                }
            },
            ABORT_CLEAN(state, id) {
                state.aborted = state.aborted.filter(item => item !== id);
            },
            SET_UNREAD_NOTIFICATIONS_COUNT(state, count = 0) {
                state.unread_notifications_count = count;
            }
        }
    }
};
